'use client';

import { Loader2Icon, PauseIcon, PlayIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import type { VoiceGender } from '@/features/chat/lib/voices';
import { cn } from '@/lib/utils';

const VOICE_STORAGE_KEY = 'chat-voice';

/**
 * Compact player for the pre-generated ElevenLabs narrations in
 * /public/audio/narration. `name` maps to `<name>-<voice>.mp3`.
 */
export function NarrationPlayer({
  name,
  label = 'AI narration',
  className,
}: {
  name: string;
  label?: string;
  className?: string;
}) {
  const [voice, setVoice] = useState<VoiceGender>('male');
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const saved = window.localStorage.getItem(VOICE_STORAGE_KEY);
    if (saved === 'male' || saved === 'female') setVoice(saved);
  }, []);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const src = `/audio/narration/${name}-${voice}.mp3`;

  const stop = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(false);
    setProgress(0);
  };

  const fail = () => {
    stop();
    toast.error('Audio is unavailable right now.');
  };

  const toggle = async () => {
    if (loading) return;
    if (playing) {
      // True pause: keep the element and position for resume.
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    if (audioRef.current) {
      // Resume from where we paused.
      try {
        await audioRef.current.play();
        setPlaying(true);
      } catch {
        fail();
      }
      return;
    }
    setLoading(true);
    try {
      const audio = new Audio(src);
      audioRef.current = audio;
      audio.ontimeupdate = () => {
        if (audio.duration > 0) {
          setProgress(audio.currentTime / audio.duration);
        }
      };
      audio.onended = stop;
      audio.onerror = () => {
        if (audioRef.current === audio) fail();
      };
      await audio.play();
      setPlaying(true);
    } catch {
      fail();
    } finally {
      setLoading(false);
    }
  };

  const changeVoice = (next: VoiceGender) => {
    if (next === voice) return;
    stop();
    setVoice(next);
    window.localStorage.setItem(VOICE_STORAGE_KEY, next);
  };

  const seek = (value: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) {
      return;
    }
    const next = value / 100;
    audio.currentTime = next * audio.duration;
    setProgress(next);
  };

  return (
    <div
      className={cn(
        'relative flex items-center gap-2 overflow-hidden rounded-full border border-edge bg-muted/30 py-1 pr-1 pl-1',
        className
      )}
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? `Pause ${label}` : `Play ${label}`}
        className="relative flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition-transform after:absolute after:-inset-2.5 hover:scale-105"
      >
        {loading ? (
          <Loader2Icon className="size-3 animate-spin" />
        ) : playing ? (
          <PauseIcon className="size-3" />
        ) : (
          <PlayIcon className="size-3 translate-x-px" />
        )}
      </button>

      <span className="font-mono text-xs text-muted-foreground select-none">
        {label}
      </span>

      <div className="flex rounded-full border border-edge p-0.5 font-mono text-[10px]">
        {(['male', 'female'] as const).map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={voice === v}
            onClick={() => changeVoice(v)}
            className={cn(
              'relative rounded-full px-2.5 py-1 transition-colors after:absolute after:inset-x-0 after:-inset-y-3',
              voice === v
                ? 'bg-foreground text-background'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {v === 'male' ? 'Brian' : 'Sarah'}
          </button>
        ))}
      </div>

      {progress > 0 && (
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={Math.round(progress * 100)}
          onChange={(e) => seek(Number(e.target.value))}
          aria-label={`${label} progress`}
          aria-valuetext={`${Math.round(progress * 100)}% played`}
          className="absolute inset-x-0 bottom-0 h-0.5 w-full cursor-pointer appearance-none bg-transparent text-foreground/70 [&::-moz-range-thumb]:size-0 [&::-moz-range-thumb]:border-0 [&::-webkit-slider-thumb]:size-0 [&::-webkit-slider-thumb]:appearance-none"
          style={{
            background: `linear-gradient(to right, currentColor ${progress * 100}%, transparent ${progress * 100}%)`,
          }}
        />
      )}
    </div>
  );
}
